export type MobilePlatform = "ios" | "android" | "other";

export const NARROW_VIEWPORT_MEDIA_QUERY = "(max-width: 767px)";

export interface MobileContext {
  isMobileBrowser: boolean;
  /** True when viewport width ≤ 767px (responsive mobile layout). */
  isNarrowViewport: boolean;
  platform: MobilePlatform;
  isStandalone: boolean;
}

/** Mobile web layout or phone/tablet viewport — used for top-bar slides marked `mobileOnly`. */
export const isMobileAnnouncementTarget = (ctx: MobileContext): boolean =>
  ctx.isMobileBrowser || ctx.isNarrowViewport;

export const matchesNarrowViewport = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return window.matchMedia(NARROW_VIEWPORT_MEDIA_QUERY).matches;
};

const IOS_USER_AGENT = /iphone|ipad|ipod/i;
const ANDROID_USER_AGENT = /android/i;

const getStandaloneStatus = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || navigatorWithStandalone.standalone === true;
};

const getMobilePlatform = (userAgent: string): MobilePlatform => {
  if (IOS_USER_AGENT.test(userAgent)) {
    return "ios";
  }

  if (ANDROID_USER_AGENT.test(userAgent)) {
    return "android";
  }

  return "other";
};

export const getMobileContext = (): MobileContext => {
  if (typeof window === "undefined") {
    return {
      isMobileBrowser: false,
      isNarrowViewport: false,
      platform: "other",
      isStandalone: false,
    };
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  const platform = getMobilePlatform(userAgent);
  const isMobileBrowser = platform === "ios" || platform === "android";

  return {
    isMobileBrowser,
    isNarrowViewport: matchesNarrowViewport(),
    platform,
    isStandalone: getStandaloneStatus(),
  };
};
